/* istanbul ignore file -- @preserve */
import { useCallback, useEffect, useRef, useState } from 'react';

interface SnapZoneConfig {
  threshold: number;
  onSnap: () => void;
}

interface DraggablePositionOptions {
  handleRef: React.RefObject<HTMLElement | null>;
  targetRef: React.RefObject<HTMLElement | null>;
  enabled?: boolean;
  edgeMargin?: number;
  snapZone?: SnapZoneConfig;
  onDragStart?: () => void;
}

interface DraggablePositionResult {
  style: React.CSSProperties;
  isDragging: boolean;
  isInSnapZone: boolean;
  reset: () => void;
}

const DRAG_THRESHOLD = 5;

function useDraggablePosition({
  handleRef,
  targetRef,
  enabled = true,
  edgeMargin = 8,
  snapZone,
  onDragStart,
}: DraggablePositionOptions): DraggablePositionResult {
  const [position, setPosition] = useState<{ left: number; top: number } | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isInSnapZone, setIsInSnapZone] = useState(false);
  const dragState = useRef<{
    startX: number;
    startY: number;
    offsetX: number;
    offsetY: number;
    moved: boolean;
    dragStartFired: boolean;
  } | null>(null);

  const clamp = useCallback(
    (left: number, top: number) => {
      const target = targetRef.current;
      if (!target) return { left, top };
      const rect = target.getBoundingClientRect();
      const maxLeft = window.innerWidth - rect.width - edgeMargin;
      const maxTop = window.innerHeight - rect.height - edgeMargin;
      return {
        left: Math.max(edgeMargin, Math.min(left, maxLeft)),
        top: Math.max(edgeMargin, Math.min(top, maxTop)),
      };
    },
    [targetRef, edgeMargin],
  );

  const reset = useCallback(() => {
    setPosition(null);
    setIsInSnapZone(false);
  }, []);

  useEffect(() => {
    const handle = handleRef.current;
    if (!handle || !enabled) return;

    const onPointerDown = (e: PointerEvent) => {
      if ((e.target as HTMLElement).closest('button, a, [role="button"]')) return;
      const target = targetRef.current;
      if (!target) return;
      const rect = target.getBoundingClientRect();
      dragState.current = {
        startX: e.clientX,
        startY: e.clientY,
        offsetX: e.clientX - rect.left,
        offsetY: e.clientY - rect.top,
        moved: false,
        dragStartFired: false,
      };
      handle.setPointerCapture(e.pointerId);
    };

    const onPointerMove = (e: PointerEvent) => {
      const state = dragState.current;
      if (!state) return;
      const dx = e.clientX - state.startX;
      const dy = e.clientY - state.startY;
      if (!state.moved && Math.abs(dx) < DRAG_THRESHOLD && Math.abs(dy) < DRAG_THRESHOLD) return;
      state.moved = true;

      if (!state.dragStartFired) {
        state.dragStartFired = true;
        setIsDragging(true);
        document.body.style.userSelect = 'none';
        if (onDragStart) onDragStart();
      }

      const newLeft = e.clientX - state.offsetX;
      const newTop = e.clientY - state.offsetY;
      const clamped = clamp(newLeft, newTop);
      setPosition(clamped);

      // snap zone detection
      if (snapZone) {
        const target = targetRef.current;
        if (target) {
          const targetRight = clamped.left + target.getBoundingClientRect().width;
          const distFromRight = window.innerWidth - targetRight;
          setIsInSnapZone(distFromRight < snapZone.threshold);
        }
      }
    };

    const onPointerUp = (e: PointerEvent) => {
      const state = dragState.current;
      if (!state) return;
      handle.releasePointerCapture(e.pointerId);
      dragState.current = null;

      if (isInSnapZone && snapZone && state.moved) {
        snapZone.onSnap();
        setPosition(null);
      }

      setIsDragging(false);
      setIsInSnapZone(false);
      document.body.style.userSelect = '';
    };

    handle.addEventListener('pointerdown', onPointerDown);
    handle.addEventListener('pointermove', onPointerMove);
    handle.addEventListener('pointerup', onPointerUp);

    return () => {
      handle.removeEventListener('pointerdown', onPointerDown);
      handle.removeEventListener('pointermove', onPointerMove);
      handle.removeEventListener('pointerup', onPointerUp);
      document.body.style.userSelect = '';
    };
  }, [handleRef, targetRef, enabled, clamp, snapZone, onDragStart, isInSnapZone]);

  // Clamp on resize
  useEffect(() => {
    if (!position || !enabled) return;
    const onResize = () => setPosition((prev) => (prev ? clamp(prev.left, prev.top) : null));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [position, enabled, clamp]);

  const style: React.CSSProperties = position
    ? { left: position.left, top: position.top, right: 'auto', bottom: 'auto' }
    : {};

  return { style, isDragging, isInSnapZone, reset };
}

export { useDraggablePosition };
